/**
 * The operator screen (container, PLAN §5.2–5.3, D3, D19, D25): the live
 * playout session (`api/playout.ts`) in, the presentational playout
 * components out. It owns the page's UI state — the part cursor, the tab,
 * the open dialogs — and turns key presses (`lib/playout/hotkeys.ts` →
 * `commands.ts`) and the components' events into commands. Commands are
 * never optimistic: the TAKE button shows pending until the ack, a nack is a
 * toast with its reason.
 *
 * Around the running order: the tenant's alerts about this studio and site
 * (`lib/playout/alertBar.ts`, hide for me / acknowledge for everyone, an
 * optional cue for a critical one), the keymap in three layers
 * (`lib/playout/keymap.ts`: built in, the show style's, the user's own in
 * this browser) with its editor, a dismissable D19 banner, and the
 * confirmations `lib/playout/safety.ts` asks for. On the backup's own screen, when main stops answering, D40's
 * take-over (`lib/takeOver.ts`).
 */
import type { PlayoutStateWire } from '@norna/api-contract/playout.js';
import type { ProductionWire } from '@norna/api-contract/productions.js';
import { DEFAULT_MINIMUM_TAKE_SPAN_MS, type ProductionConfig } from '@norna/playout-engine/productionConfig.js';
import type { AlertWire } from '@norna/api-contract/alerts.js';
import { BellIcon, BellOffIcon, KeyboardIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { PlayoutNotice, PlayoutSession, PlayoutSnapshot } from '../api/playout.js';
import {
  useAcknowledgeAlert,
  useAlerts,
  useAsRun,
  useConnectivity,
  useGrants,
  useNow,
  usePlayoutSession,
  usePlayoutSnapshot,
  useProduction,
  useRedundancyOverview,
  useServices,
  useSession,
  useSwitchover,
  useTakeOver,
} from '../app/hooks.js';
import type { ConnectivityState } from '../connectivity/state.js';
import { ForcePromoteDialog } from '../components/redundancy/ForcePromoteDialog.js';
import { TakeOverBanner } from '../components/playout/TakeOverBanner.js';
import { takeOverOffer } from '../lib/takeOver.js';
import { selfNodeIdOf } from '../lib/redundancy.js';
import type { StoragePort } from '../auth/session.js';
import { AlertBar } from '../components/playout/AlertBar.js';
import { ConfirmDialog } from '../components/playout/ConfirmDialog.js';
import { KeymapDialog } from '../components/playout/KeymapDialog.js';
import { AdlibPanel, type PieceTypeChoice } from '../components/playout/AdlibPanel.js';
import { AsRunList } from '../components/playout/AsRunList.js';
import { DevicePanel } from '../components/playout/DevicePanel.js';
import { OnAirUpdateBanner } from '../components/playout/OnAirUpdateBanner.js';
import { PairBadge } from '../components/playout/PairBadge.js';
import { StandbyPeekPanel } from './StandbyPeekPanel.js';
import { PlayoutControls } from '../components/playout/PlayoutControls.js';
import { PlayoutHeader, type PlayoutHeaderProps } from '../components/playout/PlayoutHeader.js';
import { PlayoutRundown } from '../components/playout/PlayoutRundown.js';
import { TakeButton } from '../components/playout/TakeButton.js';
import { Button } from '../components/ui/button.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.js';
import { actionsOf, adlibPanelsOf, type RundownAdlibSection, rundownAdlibsOf } from '../lib/playout/adlib.js';
import { lastTakeOf, liveTiming, productionNow } from '../lib/playout/clock.js';
import { type CommandInput, intentOutcome } from '../lib/playout/commands.js';
import { type FocusKind, type HotkeyContext, type Intent, resolveHotkey, type TakeGuard, takeGuard, takeView } from '../lib/playout/hotkeys.js';
import { hotkeyContextOf, keymapItemsOf, keysFor, parseUserKeymap, resolveKeymap, showStyleKeymapOf, type UserKeymap, userKeymapStorageKey } from '../lib/playout/keymap.js';
import { ALERT_SOUND_STORAGE_KEY, barAlerts, criticalCue, HIDDEN_ALERTS_STORAGE_KEY, hideKey } from '../lib/playout/alertBar.js';
import { backwardTake, type Confirmation, confirmationFor, layersPlaying } from '../lib/playout/safety.js';
import { playCue, type ToneContext } from '../lib/alertTone.js';
import { parseDismissals, withDismissal } from '../lib/dismissals.js';
import { definitionIn, type LiveEdit, pendingLiveEdit } from '../lib/playout/liveEdit.js';
import { type PartView, playoutView, type PlayoutView as PlayoutViewModel, type SourceLayer, sourceLayersOf } from '../lib/playout/viewModel.js';
import { allows } from '../lib/permissions.js';

/** How often the countdowns re-render. */
const TICK_MS = 250;

/** What the TAKE button says when next is earlier than what is on air (`backwardTake`), or null. */
function takeWarning(view: PlayoutViewModel): string | null {
  return backwardTake(view.partOrder, view.onAirPartId, view.nextPartId) ? 'Next is earlier in the running order: a take re-airs it.' : null;
}

function notifyToast(notice: PlayoutNotice): void {
  if (notice.level === 'error') toast.error(notice.message);
  else toast(notice.message);
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** The input types a user types into. */
const TYPING_INPUTS: ReadonlySet<string> = new Set(['text', 'search', 'number', 'email', 'url', 'tel', 'password', 'date', 'time', 'datetime-local']);

/** Where a key press landed. A dialog owns its keys (a confirmation's Enter is not a take); typing is never a hotkey. */
function focusKind(target: EventTarget | null): FocusKind {
  if (!(target instanceof HTMLElement)) return 'none';
  if (target.closest('[role="dialog"], [role="alertdialog"]') !== null) return 'text';
  if (target.isContentEditable || target.tagName === 'TEXTAREA') return 'text';
  // Only typing is text: a checkbox, a radio or a dropdown is a control (F12 and the item hotkeys still fire there).
  if (target instanceof HTMLInputElement) return TYPING_INPUTS.has(target.type) ? 'text' : 'control';
  return target.closest('button, a, select, [role="tab"]') === null ? 'none' : 'control';
}

export function PlayoutView({ productionId }: { readonly productionId: string }) {
  const session = usePlayoutSession(productionId, notifyToast);
  const snap = usePlayoutSnapshot(session);
  // The record, live (the change socket re-reads it): a production ended elsewhere ends this screen at once.
  const record = useProduction(productionId).data;
  useEffect(() => {
    if (record !== undefined) session.productionChanged(record);
  }, [session, record]);
  if (snap.status === 'failed') {
    return (
      <div className="mx-auto grid max-w-xl gap-4 p-6" role="alert" data-testid="playout-failed">
        <p className="text-sm text-destructive">Could not open the production: {snap.error}</p>
        <Button variant="outline" onClick={() => session.retry()} className="w-fit">
          Retry
        </Button>
      </div>
    );
  }
  if (snap.production === null) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground" data-testid="playout-loading">
        Loading the production…
      </p>
    );
  }
  return <PlayoutScreen session={session} snap={snap} production={snap.production} />;
}

function AsRunTab({ productionId }: { readonly productionId: string }) {
  const asRun = useAsRun(productionId);
  return (
    <div className="p-4">
      <AsRunList
        events={(asRun.data?.pages ?? []).flatMap((p) => p.events)}
        loading={asRun.isFetching}
        error={asRun.error === null ? null : messageOf(asRun.error)}
        hasMore={asRun.hasNextPage}
        onLoadMore={() => {
          void asRun.fetchNextPage();
        }}
        onRefresh={() => {
          void asRun.refetch();
        }}
      />
    </div>
  );
}

/** What the config snapshot offers the screen: lanes, panels, actions, piece types. */
function useConfigViews(config: ProductionConfig | null) {
  return useMemo(() => {
    if (config === null) return { layers: [], panels: [], actions: [], pieceTypes: [] };
    const pieceTypes: PieceTypeChoice[] = Object.entries(config.pieceTypes).map(([id, t]) => ({ id, label: t.label === '' ? id : t.label, params: t.params }));
    return { layers: sourceLayersOf(config.sourceLayers), panels: adlibPanelsOf(config), actions: actionsOf(config), pieceTypes };
  }, [config]);
}

function partsOf(view: PlayoutViewModel): PartView[] {
  return view.rundowns.flatMap((r) => r.segments.flatMap((s) => s.parts));
}

/** The take guard over the snapshot, at the browser's `now`. */
function guardOf(snap: PlayoutSnapshot, config: ProductionConfig | null, permitted: boolean, now: number): TakeGuard {
  const state = snap.state;
  return takeGuard({
    active: state !== null && state.activation.status !== 'inactive',
    hasNext: (state?.next ?? null) !== null,
    pending: snap.pending.some((p) => p.type === 'take'),
    lastTakeTime: state === null ? null : lastTakeOf(state),
    productionNowMs: state === null ? 0 : productionNow(state.timing, snap.stateAt, now),
    minimumTakeSpanMs: config?.settings.minimumTakeSpanMs ?? DEFAULT_MINIMUM_TAKE_SPAN_MS,
    permitted,
  });
}

/**
 * One key listener for the page's life. It reads the latest render's
 * context through a ref, updated after each render.
 */
function useHotkeys(current: { readonly context: HotkeyContext; readonly selected: string | null; readonly enabled: boolean; readonly run: (intent: Intent) => void }): void {
  const latest = useRef(current);
  useEffect(() => {
    latest.current = current;
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const now = latest.current;
      if (!now.enabled) return;
      const press = { key: event.key, code: event.code, shift: event.shiftKey, ctrl: event.ctrlKey, alt: event.altKey, meta: event.metaKey, repeat: event.repeat, focus: focusKind(event.target) };
      const { intent } = resolveHotkey(press, now.context, now.selected);
      if (intent === null) return;
      event.preventDefault();
      now.run(intent);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

type BannersProps = {
  readonly production: ProductionWire;
  readonly snap: PlayoutSnapshot;
  /** D19's pending live edit. */
  readonly liveEdit: LiveEdit | null;
  readonly canTake: boolean;
  readonly send: (command: CommandInput) => void;
  readonly onRetry: () => void;
};

function Banners({ production, snap, liveEdit: pendingEdit, canTake, send, onRetry }: BannersProps) {
  const current = snap.state?.current ?? null;
  // D19's banner, dismissed for this session: this occurrence stays away; another edit (or part) brings it back.
  const [dismissed, setDismissed] = useState<string | null>(null);
  const liveEdit = pendingEdit === null || pendingEdit.occurrence === dismissed ? null : pendingEdit;
  return (
    <>
      {production.status === 'ended' ? (
        <div role="status" className="border-b bg-muted px-4 py-2 text-sm" data-testid="production-ended">
          This production has ended{production.endReason === null ? '' : `: ${production.endReason}`}. It takes no more commands.
        </div>
      ) : null}
      {snap.stateError === null ? null : (
        <div role="alert" className="flex items-center gap-3 border-b border-destructive px-4 py-2 text-sm text-destructive" data-testid="playout-state-error">
          <span className="flex-1">No live playout state: {snap.stateError}</span>
          <Button size="sm" variant="outline" onClick={onRetry} data-testid="playout-retry">
            Retry
          </Button>
        </div>
      )}
      {snap.configError === null ? null : (
        <div role="alert" className="border-b border-warning bg-warning px-4 py-2 text-sm text-warning-foreground">
          The config snapshot does not parse; adlibs and actions are unavailable. {snap.configError}
        </div>
      )}
      {liveEdit === null || current === null ? null : (
        <OnAirUpdateBanner
          partTitle={current.instance.part.title}
          fields={liveEdit.fields}
          canUpdate={canTake}
          pending={snap.pending.some((p) => p.type === 'syncToInstance')}
          onUpdate={() => send({ type: 'syncToInstance', partInstanceId: liveEdit.partInstanceId, fields: [...liveEdit.fields] })}
          onDismiss={() => setDismissed(liveEdit.occurrence)}
        />
      )}
    </>
  );
}

/** The main/backup pair (PLAN §8.6), when the production runs in one: who is active, and the manual switchover. */
function Pair({ production, state, canSwitch, onAirTitle }: { readonly production: ProductionWire; readonly state: PlayoutStateWire | null; readonly canSwitch: boolean; readonly onAirTitle: string | null }) {
  const switchover = useSwitchover();
  const [asking, setAsking] = useState(false);
  const redundancy = state?.redundancy;
  if (redundancy === undefined) return null;
  const confirmation = confirmationFor({ kind: 'switchover', toNode: redundancy.peerNode ?? 'the other node' }, { onAirTitle, rehearsal: state?.activation.status === 'rehearsal', layersPlaying: new Set() });
  const onSwitchover = (): void => {
    setAsking(false);
    switchover.mutate(
      { productionId: production.productionId },
      {
        onSuccess: (result) => toast(`Switched over: ${result.from} → ${result.to} (term ${String(result.term)}) in ${String(result.durationMs)} ms`),
        onError: (err) => toast.error(`Switchover refused: ${messageOf(err)}`),
      },
    );
  };
  return (
    <>
      <PairBadge redundancy={redundancy} canSwitch={canSwitch} switching={switchover.isPending} onSwitchover={() => setAsking(true)} />
      {asking && confirmation !== null ? <Confirm confirmation={confirmation} onCancel={() => setAsking(false)} onConfirm={onSwitchover} /> : null}
    </>
  );
}

/** Main's reach as this page probes it (its endpoint's `/health`), or null when the page does not probe it. */
function mainAnswersOf(connectivity: ConnectivityState, mainNode: string | null): boolean | null {
  const endpoint = connectivity.endpoints.find((e) => e.node?.nodeId === mainNode);
  if (endpoint === undefined || endpoint.reach === 'unknown') return null;
  return endpoint.reach === 'up';
}

/** This node's own peer link, as its `/health` says (its pair summary), or null when unknown. */
function peerLinkUpOf(connectivity: ConnectivityState): boolean | null {
  const link = connectivity.endpoints.find((e) => e.endpoint.id === connectivity.primaryId)?.node?.pair?.peerLink;
  return link === 'connected' ? true : link === 'disconnected' ? false : null;
}

/**
 * D40's way out on the BACKUP's screen: main stopped answering (`lib/takeOver.ts`), so one click takes the production
 * over — the emergency activation's confirmation (the name and a reason), then the take-over (`useTakeOver`: forced
 * on air here, or switched over when main still answers the pair).
 */
function TakeOver({ production, state, lastCommandLost, canTakeOver }: { readonly production: ProductionWire; readonly state: PlayoutStateWire | null; readonly lastCommandLost: boolean; readonly canTakeOver: boolean }) {
  const connectivity = useConnectivity();
  const offer = takeOverOffer({
    redundancy: state?.redundancy,
    selfNodeId: selfNodeIdOf(connectivity),
    mainAnswers: mainAnswersOf(connectivity, state?.redundancy?.activeNode ?? null),
    peerLinkUp: peerLinkUpOf(connectivity),
    lastCommandLost,
    ended: production.status === 'ended',
  });
  if (!offer.offer) return null;
  return <TakeOverOffered production={production} mainNode={offer.mainNode} why={offer.why} canTakeOver={canTakeOver} standbyNode={selfNodeIdOf(connectivity) ?? '?'} />;
}

function TakeOverOffered({ production, mainNode, why, canTakeOver, standbyNode }: { readonly production: ProductionWire; readonly mainNode: string; readonly why: string; readonly canTakeOver: boolean; readonly standbyNode: string }) {
  const takeOver = useTakeOver();
  const [asking, setAsking] = useState(false);
  // The name to type is the one the node checks: the overview's (its rundown's or playlist's), else its id.
  const overview = useRedundancyOverview(production.siteId);
  const name = overview.data?.sites.flatMap((s) => s.productions).find((p) => p.productionId === production.productionId)?.name ?? production.productionId;
  return (
    <>
      <TakeOverBanner
        mainNode={mainNode}
        why={why}
        canTakeOver={canTakeOver}
        pending={takeOver.isPending}
        onTakeOver={() => {
          takeOver.reset();
          setAsking(true);
        }}
      />
      <ForcePromoteDialog
        open={asking}
        confirmation={name}
        activeNode={mainNode}
        standbyNode={standbyNode}
        pending={takeOver.isPending}
        error={takeOver.error === null ? null : messageOf(takeOver.error)}
        onConfirm={({ confirm, reason }) =>
          takeOver.mutate(
            { productionId: production.productionId, confirm, reason },
            {
              onSuccess: (outcome) => {
                setAsking(false);
                if (outcome.kind === 'forced') toast.warning(`Taken over: FORCED on air on ${outcome.result.to} at term ${String(outcome.result.term)}. ${outcome.result.warning}`, { duration: 20_000 });
                else toast(`Taken over: switched over ${outcome.result.from} → ${outcome.result.to} (term ${String(outcome.result.term)}) in ${String(outcome.result.durationMs)} ms`);
              },
            },
          )
        }
        onOpenChange={(open) => (open ? undefined : setAsking(false))}
      />
    </>
  );
}

/** A confirmation from `lib/playout/safety.ts`, open. */
function Confirm({ confirmation, onCancel, onConfirm }: { readonly confirmation: Confirmation; readonly onCancel: () => void; readonly onConfirm: () => void }) {
  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      title={confirmation.title}
      description={confirmation.description}
      confirmLabel={confirmation.confirmLabel}
      tone={confirmation.tone}
      pending={false}
      guarded={confirmation.guarded}
      onConfirm={onConfirm}
    />
  );
}

type Clock = { readonly elapsedMs: number; readonly remainingMs: number | null };

/** The clock bar's props from the live timings and the view. */
function headerOf(snap: PlayoutSnapshot, production: ProductionWire, timing: PlayoutStateWire['timing'] | null, parts: readonly PartView[], onAirClock: Clock | null): PlayoutHeaderProps {
  const onAirTitle = parts.find((p) => p.status === 'onair')?.title ?? null;
  return {
    productionLabel: `${snap.rundowns.map((r) => r.name).join(' · ')} · ${production.studioId}`,
    activation: snap.state?.activation.status ?? 'inactive',
    onAir: onAirClock === null || onAirTitle === null ? null : { title: onAirTitle, ...onAirClock },
    nextTitle: parts.find((p) => p.status === 'next')?.title ?? null,
    mode: timing?.mode ?? 'none',
    remainingMs: timing?.remainingMs ?? 0,
    overUnderMs: timing?.overUnderMs ?? null,
    expectedEnd: timing?.expectedEnd ?? null,
    projectedEnd: timing?.projectedEnd ?? null,
    connection: snap.connection,
  };
}

/** A value kept in this browser's storage, read once and written through. */
function useStored<T>(storage: StoragePort, key: string, parse: (raw: string | null) => T, serialise: (value: T) => string): readonly [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => parse(storage.get(key)));
  const set = (next: T): void => {
    setValue(next);
    storage.set(key, serialise(next));
  };
  return [value, set];
}

const parseSound = (raw: string | null): boolean => raw === 'on';
const serialiseSound = (on: boolean): string => (on ? 'on' : 'off');
const serialiseList = (list: readonly string[]): string => JSON.stringify(list);
const serialiseKeymap = (keymap: UserKeymap): string => JSON.stringify(keymap);

/** The page's one audio context for the cue, made on first use (null where the browser has no Web Audio). */
function useToneContext(): () => ToneContext | null {
  const ref = useRef<ToneContext | null>(null);
  return () => {
    if (ref.current === null && typeof window !== 'undefined' && typeof window.AudioContext === 'function') ref.current = new window.AudioContext();
    return ref.current;
  };
}

/**
 * Sound the cue when a critical alert appears on this screen that it has not shown before (and the cue is on). The
 * first LOADED list is only looked at: before the alerts arrive the list is empty, and taking that as "seen" would
 * sound every critical alert already there the moment they load.
 */
function useCriticalCue(shown: readonly AlertWire[], loaded: boolean, sound: boolean): void {
  const seen = useRef<ReadonlySet<string> | null>(null);
  const tone = useToneContext();
  useEffect(() => {
    if (!loaded) return;
    const cue = criticalCue(shown, seen.current);
    seen.current = cue.seen;
    if (cue.sound && sound) playCue(tone());
  });
}

/** The alert bar: the tenant's active alerts about this production's studio and site (cloud only; a node keeps none). */
function OperatorAlerts({ production, sound }: { readonly production: ProductionWire; readonly sound: boolean }) {
  const alerts = useAlerts('active');
  const grants = useGrants();
  const { storage } = useServices();
  const acknowledge = useAcknowledgeAlert();
  const [hidden, setHidden] = useStored(storage, HIDDEN_ALERTS_STORAGE_KEY, parseDismissals, serialiseList);
  const list = alerts.data;
  const shown = useMemo(
    () => (list === undefined || !list.available ? [] : barAlerts(list.alerts, { siteId: production.siteId, studioId: production.studioId }, hidden)),
    [list, production.siteId, production.studioId, hidden],
  );
  useCriticalCue(shown, list !== undefined, sound);
  const acknowledgeable = new Set(shown.filter((a) => allows(grants, 'playout.take', { siteId: a.siteId, studioId: a.studioId })).map((a) => a.key));
  const pending = new Set(acknowledge.isPending && acknowledge.variables !== undefined ? [acknowledge.variables] : []);
  return (
    <AlertBar
      alerts={shown}
      acknowledgeable={acknowledgeable}
      pending={pending}
      onHide={(alert) => setHidden(withDismissal(hidden, hideKey(alert)))}
      onAcknowledge={(alert) => acknowledge.mutate(alert.key, { onError: (err) => toast.error(`Could not acknowledge: ${messageOf(err)}`) })}
    />
  );
}

/** The three keymap layers for this production and user, and the reducer's tables from them. */
function useKeymap(production: ProductionWire, panels: ReturnType<typeof adlibPanelsOf>) {
  const { storage } = useServices();
  const session = useSession();
  const showStyleKeymap = useMemo(() => showStyleKeymapOf(production.snapshot), [production.snapshot]);
  const items = useMemo(() => keymapItemsOf(panels), [panels]);
  const [user, setUser] = useStored(storage, userKeymapStorageKey(session?.userId ?? 'anonymous', production.showStyleId), parseUserKeymap, serialiseKeymap);
  const entries = useMemo(() => resolveKeymap(showStyleKeymap, items, user), [showStyleKeymap, items, user]);
  const tables = useMemo(() => hotkeyContextOf(entries), [entries]);
  return { showStyleKeymap, items, user, setUser, tables, takeKeys: keysFor(entries, 'take', true) };
}

/**
 * The stop-a-layer confirmation, when one is asked for. The question is fixed when it is asked: the piece ending
 * meanwhile must not close the dialog under the operator's hand (nor leave the keys disabled behind a vanished one).
 */
function useStopLayer(state: PlayoutStateWire | null, layers: readonly SourceLayer[], onAirTitle: string | null, send: (command: CommandInput) => void) {
  const [asking, setAsking] = useState<{ readonly layer: string; readonly confirmation: Confirmation } | null>(null);
  const stop = (layer: string): void => {
    const ctx = { onAirTitle, rehearsal: state?.activation.status === 'rehearsal', layersPlaying: layersPlaying(state?.timing.pieces ?? []) };
    const confirmation = confirmationFor({ kind: 'stop-layer', layer, layerLabel: layers.find((l) => l.id === layer)?.label ?? layer }, ctx);
    if (confirmation === null) send({ type: 'stopPiecesOnLayer', layer });
    else setAsking({ layer, confirmation });
  };
  const dialog =
    asking === null ? null : (
      <Confirm
        confirmation={asking.confirmation}
        onCancel={() => setAsking(null)}
        onConfirm={() => {
          setAsking(null);
          send({ type: 'stopPiecesOnLayer', layer: asking.layer });
        }}
      />
    );
  return { stop, dialog, open: asking !== null };
}

/** The row beside the tabs: the alert cue's switch and the keymap editor's button. */
function ScreenToolbar({ sound, onSound, onShortcuts }: { readonly sound: boolean; readonly onSound: (on: boolean) => void; readonly onShortcuts: () => void }) {
  return (
    <>
      <Button variant="ghost" size="sm" className="ml-auto" aria-pressed={sound} onClick={() => onSound(!sound)} data-testid="alert-sound" title="A chime when a critical alert appears on this screen">
        {sound ? <BellIcon /> : <BellOffIcon />}
        Alert sound {sound ? 'on' : 'off'}
      </Button>
      <Button variant="ghost" size="sm" onClick={onShortcuts} data-testid="keymap-open">
        <KeyboardIcon />
        Shortcuts
      </Button>
    </>
  );
}

/** The keymap editor over the user's layer; saving writes it to this browser. */
function ShortcutsDialog({ keymap, onClose }: { readonly keymap: ReturnType<typeof useKeymap>; readonly onClose: () => void }) {
  return (
    <KeymapDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      showStyleKeymap={keymap.showStyleKeymap}
      items={keymap.items}
      user={keymap.user}
      storageNote="Yours are kept in this browser, for you and this show style."
      onSave={(user) => {
        keymap.setUser(user);
        onClose();
        toast('Shortcuts saved in this browser.');
      }}
    />
  );
}

type ControlsAsideProps = {
  readonly snap: PlayoutSnapshot;
  readonly production: ProductionWire;
  readonly guard: TakeGuard;
  readonly nextTitle: string | null;
  readonly takeKeys: readonly string[];
  readonly takeWarning: string | null;
  readonly permissions: { readonly canActivate: boolean; readonly canTake: boolean; readonly canCommand: boolean };
  readonly config: ReturnType<typeof useConfigViews>;
  /** The rundown's adlib rows the operator can play now (the on-air and next parts', the global ones). */
  readonly rundownAdlibs: readonly RundownAdlibSection[];
  readonly onAirTitle: string | null;
  readonly nowMs: number;
  readonly run: (intent: Intent) => void;
  readonly send: (command: CommandInput) => void;
  readonly onStopLayer: (layer: string) => void;
};

/** The column beside the running order: TAKE, the lifecycle and next controls, the adlibs, the devices. */
function ControlsAside({ snap, production, guard, nextTitle, takeKeys, takeWarning: warning, permissions, config, rundownAdlibs, onAirTitle, nowMs, run, send, onStopLayer }: ControlsAsideProps) {
  const state = snap.state;
  return (
    <aside className="grid w-80 shrink-0 content-start gap-5 overflow-y-auto border-l bg-card p-3" aria-label="Playout controls">
      <TakeButton state={takeView(guard, snap.lastOutcome?.type === 'take' ? snap.lastOutcome : null)} nextTitle={nextTitle} hotkeys={takeKeys} warning={warning} onTake={() => run({ type: 'take' })} />
      <PlayoutControls
        status={state?.activation.status ?? 'inactive'}
        rehearsalProduction={production.rehearsal}
        canActivate={permissions.canActivate}
        canTake={permissions.canTake}
        pending={snap.pending.some((p) => p.type === 'activate' || p.type === 'deactivate' || p.type === 'reset')}
        onActivate={(rehearsal) => send({ type: 'activate', rehearsal })}
        onDeactivate={() => send({ type: 'deactivate' })}
        onReset={() => send({ type: 'reset' })}
        onMoveNext={(delta) => send({ type: 'moveNext', delta })}
        onDisableNextPiece={() => send({ type: 'disableNextPiece' })}
        onAirTitle={onAirTitle}
      />
      <AdlibPanel
        rundownAdlibs={rundownAdlibs}
        onRundownAdlib={(adlibId, mode) => send({ type: 'adlibFromRow', adlibId, mode })}
        panels={config.panels}
        actions={config.actions}
        pieceTypes={config.pieceTypes}
        layers={config.layers}
        enabled={permissions.canCommand}
        onAdlib={(request) => send({ type: 'adlib', ...request })}
        onAction={(actionId) => send({ type: 'executeAction', actionId })}
        onStopLayer={onStopLayer}
      />
      <section aria-label="Devices" className="grid gap-2">
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground">Devices</h3>
        <DevicePanel devices={snap.devices} available={state !== null} nowMs={nowMs} />
      </section>
    </aside>
  );
}

/** What the user may do on this production now: nothing once it has ended; next and adlibs only while active. */
function permissionsOf(grants: ReturnType<typeof useGrants>, production: ProductionWire, state: PlayoutStateWire | null) {
  const resource = { siteId: production.siteId, studioId: production.studioId };
  const live = production.status !== 'ended';
  const canTake = live && allows(grants, 'playout.take', resource);
  const active = state !== null && state.activation.status !== 'inactive';
  return {
    canTake,
    canActivate: live && allows(grants, 'playout.activate', resource) && state !== null,
    canCommand: canTake && active,
    canSwitch: live && allows(grants, 'node.switchover', resource),
  };
}

export type ScreenProps = { readonly session: PlayoutSession; readonly snap: PlayoutSnapshot; readonly production: ProductionWire };

/** The live screen, once the production is loaded (exported for the unit layer's render). */
export function PlayoutScreen({ session, snap, production }: ScreenProps) {
  const grants = useGrants();
  const now = useNow(TICK_MS);
  const { nowMs, storage } = useServices();
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState('playout');
  const [keymapOpen, setKeymapOpen] = useState(false);
  const [sound, setSound] = useStored(storage, ALERT_SOUND_STORAGE_KEY, parseSound, serialiseSound);
  const { config, state } = snap;
  const { canTake, canActivate, canCommand, canSwitch } = permissionsOf(grants, production, state);

  const { layers, panels, actions, pieceTypes } = useConfigViews(config);
  const current = state?.current ?? null;
  const onAirContext = current === null ? null : { segmentId: current.instance.segmentId, untimed: current.instance.part.untimed };
  const timing = state === null ? null : liveTiming(state.timing, snap.stateAt, now, onAirContext);
  // Rebuilt every tick: the segments' played time and pace move with the on-air clock.
  const view = playoutView(snap.rundowns, state === null || timing === null ? state : { ...state, timing }, layers);
  const guard = guardOf(snap, config, canTake, now);
  // D19: the on-air part's definition moved away from its instance in a live-editable field.
  // `snap.rundowns` is a new array whenever a row changed: the definition may have.
  const liveEdit = useMemo(
    () => (current === null ? null : pendingLiveEdit(current.instance.instanceId, current.instance.part, definitionIn(snap.rundowns, current.instance.partId))),
    [current, snap.rundowns],
  );
  // The adlib rows of the on-air and next parts (a queued adlib part has no definition, so none) and the global ones.
  const onAirPartId = current?.instance.partId ?? null;
  const nextPartId = state?.next?.instance.partId ?? null;
  const rundownAdlibs = useMemo(() => rundownAdlibsOf(snap.rundowns, onAirPartId, nextPartId, config), [snap.rundowns, onAirPartId, nextPartId, config]);

  const send = (command: CommandInput): void => void session.send(command);
  const keymap = useKeymap(production, panels);
  const onAirTitle = current?.instance.part.title ?? null;
  const stopLayer = useStopLayer(state, layers, onAirTitle, send);
  const run = (intent: Intent): void => {
    // The guard on the session's state as it is at the key press, not as last rendered: a second press in the
    // same frame must see the first take pending.
    const takeAllowed = intent.type === 'take' && guardOf(session.snapshot(), config, canTake, nowMs()).allowed;
    const outcome = intentOutcome(intent, { takeAllowed, canCommand, panels });
    if (outcome.kind === 'select') setSelected(outcome.partId);
    else if (outcome.kind === 'command') send(outcome.command);
  };
  useHotkeys({
    context: {
      ...keymap.tables,
      partOrder: view.partOrder,
      segmentOf: view.segmentOf,
      anchorPartId: view.nextPartId ?? view.onAirPartId,
    },
    selected,
    // A dialog owns the keyboard (its Enter is not a take); `focusKind` says so too, this also covers a closing one.
    enabled: tab === 'playout' && !(keymapOpen || stopLayer.open),
    run,
  });

  const onAir = timing?.onAir ?? null;
  const onAirClock = onAir === null ? null : { elapsedMs: onAir.elapsedMs, remainingMs: onAir.remainingMs };
  const header = headerOf(snap, production, timing, partsOf(view), onAirClock);

  return (
    <div className="flex h-full flex-col" data-testid="playout-view">
      <PlayoutHeader {...header} />
      <OperatorAlerts production={production} sound={sound} />
      <Pair production={production} state={state} canSwitch={canSwitch} onAirTitle={onAirTitle} />
      <TakeOver production={production} state={state} lastCommandLost={snap.lastOutcome?.reason === 'not-delivered'} canTakeOver={canActivate && canSwitch} />
      <StandbyPeekPanel productionId={production.productionId} state={state} ended={production.status === 'ended'} />
      <Banners production={production} snap={snap} liveEdit={liveEdit} canTake={canTake} send={send} onRetry={() => session.retry()} />
      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col gap-0">
        <div className="mx-4 mt-2 flex items-center gap-2">
          <TabsList>
            <TabsTrigger value="playout">Playout</TabsTrigger>
            <TabsTrigger value="asrun" data-testid="asrun-tab">
              As-run
            </TabsTrigger>
          </TabsList>
          <ScreenToolbar sound={sound} onSound={setSound} onShortcuts={() => setKeymapOpen(true)} />
        </div>
        <TabsContent value="playout" className="flex min-h-0 flex-1">
          <div className="min-w-0 flex-1">
            <PlayoutRundown
              view={view}
              selectedPartId={selected}
              onAirClock={onAirClock}
              canCommand={canCommand}
              onSelect={setSelected}
              onSetNext={(partId) => send({ type: 'setNext', partId })}
              onSetNextSegment={(segmentId) => send({ type: 'setNextSegment', segmentId })}
              onQueueSegment={(segmentId) => send({ type: 'queueNextSegment', segmentId })}
            />
          </div>
          <ControlsAside
            snap={snap}
            production={production}
            guard={guard}
            nextTitle={header.nextTitle}
            takeKeys={keymap.takeKeys}
            takeWarning={takeWarning(view)}
            permissions={{ canActivate, canTake, canCommand }}
            config={{ layers, panels, actions, pieceTypes }}
            rundownAdlibs={rundownAdlibs}
            onAirTitle={onAirTitle}
            nowMs={now}
            run={run}
            send={send}
            onStopLayer={stopLayer.stop}
          />
        </TabsContent>
        <TabsContent value="asrun" className="min-h-0 flex-1 overflow-y-auto">
          <AsRunTab productionId={production.productionId} />
        </TabsContent>
      </Tabs>
      {stopLayer.dialog}
      {keymapOpen ? <ShortcutsDialog keymap={keymap} onClose={() => setKeymapOpen(false)} /> : null}
    </div>
  );
}
